/**
 * CallFlow Leads Tab Component
 *
 * Manage leads in a CallFlow - add, remove, view attempt history.
 */

import { useEffect, useState } from 'react';
import { callFlowService, CallFlowLead } from '@/services/callFlowService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Plus, Trash2, Eye, Search, Upload } from 'lucide-react';
import { supabase } from '@/supabase/supabase';
import { CsvUploader } from '@/components/leads/CsvUploader';

interface CallFlowLeadsTabProps {
  organizationId: string;
  callflowId: string;
  callflowStatus: string;
}

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number: string;
}

export function CallFlowLeadsTab({ callflowId, organizationId, callflowStatus }: CallFlowLeadsTabProps) {
  const [leads, setLeads] = useState<CallFlowLead[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedLead, setSelectedLead] = useState<CallFlowLead | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [isLoadingAvailableLeads, setIsLoadingAvailableLeads] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [callflowId]);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      const { leads: data, summary: summaryData } = await callFlowService.listLeads(organizationId, 
        callflowId
      );
      setLeads(data);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load leads',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableLeads = async () => {
    try {
      setIsLoadingAvailableLeads(true);
      let allLeads: Lead[] = [];
      let hasMore = true;
      let offset = 0;
      const batchSize = 1000;

      // Fetch all leads in batches to bypass Supabase's row limit
      while (hasMore) {
        const { data, error } = await supabase
          .from('leads')
          .select('id, first_name, last_name, email, phone_number')
          .order('first_name')
          .range(offset, offset + batchSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allLeads = [...allLeads, ...data];
          offset += batchSize;
          
          // If we got less than batchSize, we've reached the end
          if (data.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      // Filter out leads already in this CallFlow
      const existingLeadIds = new Set(leads.map(l => l.lead_id));
      const available = allLeads.filter(l => !existingLeadIds.has(l.id));
      setAvailableLeads(available);
    } catch (error) {
      console.error('Failed to fetch available leads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available leads',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAvailableLeads(false);
    }
  };

  const handleOpenAddDialog = () => {
    setShowAddDialog(true);
    fetchAvailableLeads();
  };

  const handleAddLeads = async () => {
    if (selectedLeadIds.length === 0) return;

    try {
      setIsAdding(true);
      await callFlowService.addLeads(organizationId, callflowId, selectedLeadIds);
      toast({
        title: 'Success',
        description: `Added ${selectedLeadIds.length} lead(s) to CallFlow`,
      });
      setSelectedLeadIds([]);
      setShowAddDialog(false);
      await fetchLeads();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add leads',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveLead = async (leadId: string) => {
    if (!confirm('Are you sure you want to remove this lead?')) return;

    try {
      await callFlowService.removeLead(organizationId, callflowId, leadId);
      toast({
        title: 'Success',
        description: 'Lead removed from CallFlow',
      });
      await fetchLeads();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove lead',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-muted text-foreground',
      calling: 'bg-secondary text-foreground',
      completed: 'bg-good-bg text-good',
      failed: 'bg-danger-bg text-danger',
    };

    return <Badge className={colors[status]}>{status.toUpperCase()}</Badge>;
  };

  const canRemoveLead = () => {
    return callflowStatus !== 'active';
  };

  const showAttemptHistory = (lead: CallFlowLead) => {
    setSelectedLead(lead);
    setShowHistoryDialog(true);
  };

  const handleUploadComplete = () => {
    setShowUploadDialog(false);
    // Refresh the leads list to include newly uploaded leads
    fetchLeads();
    toast({
      title: 'Success',
      description: 'Leads uploaded and automatically added to this CallFlow.',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-faint" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{summary.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-muted-foreground">{summary.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Calling</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">{summary.calling}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-good">{summary.completed}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-danger">{summary.failed}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload CSV
        </Button>
        <Button onClick={handleOpenAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add Leads
        </Button>
      </div>

      {/* Leads Table */}
      <Card>
        <CardContent className="pt-6">
          {leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No leads in this CallFlow. Add leads to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Last Reason</TableHead>
                  <TableHead>Next Attempt</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">
                      {lead.leads ? `${lead.leads.first_name} ${lead.leads.last_name}` : 'Unknown'}
                    </TableCell>
                    <TableCell>{lead.leads?.phone_number}</TableCell>
                    <TableCell>{getStatusBadge(lead.status)}</TableCell>
                    <TableCell>{lead.attempts}</TableCell>
                    <TableCell>{lead.last_ended_reason || '-'}</TableCell>
                    <TableCell>
                      {lead.next_attempt_at
                        ? new Date(lead.next_attempt_at).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {lead.attempt_history && lead.attempt_history.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => showAttemptHistory(lead)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {canRemoveLead() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLead(lead.id)}
                            className="text-danger hover:text-danger"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Leads Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setLeadSearchQuery(''); // Reset search when dialog closes
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Leads to CallFlow</DialogTitle>
            <DialogDescription>
              Select leads to add to this CallFlow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative flex items-center">
              <Search className="absolute left-3 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={leadSearchQuery}
                onChange={(e) => setLeadSearchQuery(e.target.value)}
                className="pl-10 w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            {isLoadingAvailableLeads ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-ink-faint" />
                <span className="ml-2 text-muted-foreground">Loading all leads...</span>
              </div>
            ) : (() => {
              // Filter leads based on search query
              const filteredLeads = leadSearchQuery.trim()
                ? availableLeads.filter((lead) => {
                    const searchLower = leadSearchQuery.toLowerCase();
                    return (
                      lead.first_name?.toLowerCase().includes(searchLower) ||
                      lead.last_name?.toLowerCase().includes(searchLower) ||
                      lead.email?.toLowerCase().includes(searchLower) ||
                      lead.phone_number?.includes(leadSearchQuery)
                    );
                  })
                : availableLeads;

              if (availableLeads.length === 0) {
                return (
                  <p className="text-center text-muted-foreground py-4">
                    No available leads to add
                  </p>
                );
              }

              if (filteredLeads.length === 0) {
                return (
                  <p className="text-center text-muted-foreground py-4">
                    No leads match your search
                  </p>
                );
              }

              return (
                <div className="space-y-2">
                  {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center gap-3 p-3 border rounded hover:bg-muted cursor-pointer"
                    onClick={() => {
                      setSelectedLeadIds((prev) =>
                        prev.includes(lead.id)
                          ? prev.filter((id) => id !== lead.id)
                          : [...prev, lead.id]
                      );
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={() => {}}
                      className="h-4 w-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{`${lead.first_name} ${lead.last_name}`}</p>
                      <p className="text-sm text-muted-foreground">{lead.phone_number}</p>
                    </div>
                  </div>
                ))}
                </div>
              );
            })()}

            <div className="flex justify-between items-center pt-4">
              <p className="text-sm text-muted-foreground">
                {selectedLeadIds.length} lead(s) selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddLeads}
                  disabled={selectedLeadIds.length === 0 || isAdding}
                >
                  {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add {selectedLeadIds.length > 0 && `(${selectedLeadIds.length})`}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attempt History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Attempt History</DialogTitle>
            <DialogDescription>
              {selectedLead?.leads?.first_name} {selectedLead?.leads?.last_name} - {selectedLead?.leads?.phone_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {(Array.isArray(selectedLead?.attempt_history) ? selectedLead.attempt_history : []).map((attempt, index) => (
              <Card key={index}>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Attempt #{attempt.attempt}</p>
                      <p className="font-medium">
                        {new Date(attempt.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Ended Reason</p>
                      <p className="font-medium">{attempt.ended_reason}</p>
                    </div>
                    {attempt.delay_before_next && (
                      <div>
                        <p className="text-muted-foreground">Retry Delay</p>
                        <p className="font-medium">{attempt.delay_before_next} minutes</p>
                      </div>
                    )}
                    {attempt.error_details && (
                      <div className="col-span-2">
                        <p className="text-muted-foreground">Error Details</p>
                        <p className="font-medium text-danger">{attempt.error_details}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Leads via CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to add new leads directly to this CallFlow. The leads will be automatically added after successful upload.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <CsvUploader
              organizationId={organizationId}
              onUploadComplete={handleUploadComplete}
              callflowId={callflowId}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
